import { useState, useEffect, useRef, useMemo } from "react";
import Select from "@rio-cloud/rio-uikit/Select";
import DatePicker from "@rio-cloud/rio-uikit/DatePicker";
import SearchAutocomplete from "../../form/searchAutocomplete";
import { Dealership } from "../../../models/dealership";
import { fetchDealerships } from "../../../api/fetchDealerships";
import { ButtonDropdown, Multiselect } from "@rio-cloud/rio-uikit";
import TypeButtonGroup from "./typeButtonGroup";
import NewOccurrenceButton from "./newOccurrenceButton";
import { Occurrence } from "../../../models/occurrence";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  getAllCells,
  getAllRegionals,
  getRegionalsByCel,
  getCellsByRegional,
} from "../../../data/cellRegionalTraceability";
import { fetchCpvs } from "../../../api/fetchCpvs";
import useAssetModels from "../../../hooks/useAssetModels";
import { VEHICLE_MODEL_TYPE_LABEL, VehicleModelType, getVehicleModelType } from "../../../data/vehicleModelType";

interface IFilterRowProps {
  searchByChassis: (searchTerm: string, searchFieldName?: string) => void;
  setCriticalityFilter: (criticality: string) => void;
  setStartDateFilter: ({
    startDate,
    endDate,
  }: {
    startDate?: string | undefined;
    endDate?: string | undefined;
  }) => void;
  setUpdateDateFilter: ({
    startDate,
    endDate,
  }: {
    startDate?: string | undefined;
    endDate?: string | undefined;
  }) => void;
  setTypeFilter: (type: string[]) => void;
  typeFilter: string[];
  setIsAlertFilter: (isAlert: string) => void;
  setDealershipFilter: (dealerships: string[]) => void;
  setRegionalFilter?: (regionals: string[]) => void;
  setCellFilter?: (cells: string[]) => void;
  setModelFilter?: (models: string[]) => void;
  setCpvFilter?: (cpv: string | undefined) => void;
  persona: 'MANAGER' | 'TOWER' | 'CONSULTANT' | 'PARTNERSHIP';
  viewType: 'card' | 'list';
  setViewType: (viewType: 'card' | 'list') => void;
  finalized?: boolean;
  partners: { id: string; name: string }[];
  isValidStep: (requiredFields: string[], newOccurrence: Occurrence) => boolean;
  create?: (
    occurrence: Occurrence
  ) => Promise<{ wasCreated: boolean; error?: string }>;
  exportButton?: React.ReactNode;
  copyTokenButton?: React.ReactNode;
}

const FilterRow = (props: IFilterRowProps) => {
  const [chassis, setChassis] = useState<string | undefined>(undefined);
  const [searchFieldName, setSearchFieldName] = useState<string>('CHASSIS');
  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);
  const [updateStartDate, setUpdateStartDate] = useState<string | undefined>(
    undefined
  );
  const [updateEndDate, setUpdateEndDate] = useState<string | undefined>(
    undefined
  );
  const [dealershipsSelected, setDealershipsSelected] = useState<Dealership[]>([]);
  const [typeMaxWidth, setTypeMaxWidth] = useState<string>("100%");
  const typeContainerRef = useRef<HTMLDivElement>(null);
  const isResponsiveView = useMediaQuery('(max-width: 1024px)');
  const [showOpenDateDropdown, setShowOpenDateDropdown] = useState(false);
  const [datePickerKey, setDatePickerKey] = useState(0);
  const [endDateError, setEndDateError] = useState<string | null>(null);
  const [updateEndDateError, setUpdateEndDateError] = useState<string | null>(null);
  const [regional, setRegional] = useState<string[]>(['undefined']);
  const [celula, setCelula] = useState<string[]>(['undefined']);
  const [model, setModel] = useState<string[]>(['undefined']);
  // const [modelType, setModelType] = useState<string[]>(['undefined']); // campo de Tipo de Modelo separado — desativado, virou subfiltro dentro do Modelo (ver TYPE_OPTION_PREFIX abaixo)
  const [cpvList, setCpvList] = useState<string[]>([]);
  const [cpvSelected, setCpvSelected] = useState<string>('undefined');
  const { assetModels } = useAssetModels();
  const uniqueModelDescriptions = useMemo(() => {
    const normalized = assetModels
      .map((m) => m.description?.trim())
      .filter((description): description is string => !!description);
    return Array.from(new Set(normalized)).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [assetModels]);

  const modelDescriptionsByModelType = useMemo(() => {
    const grouped: Record<string, Set<string>> = {};
    assetModels.forEach((m) => {
      const description = m.description?.trim();
      const modelTypeOfModel = getVehicleModelType(m.modelCode);
      if (!description || !modelTypeOfModel) return;
      if (!grouped[modelTypeOfModel]) grouped[modelTypeOfModel] = new Set();
      grouped[modelTypeOfModel].add(description);
    });
    return grouped;
  }, [assetModels]);

  const availableModelTypes = useMemo(
    () => Object.keys(modelDescriptionsByModelType) as VehicleModelType[],
    [modelDescriptionsByModelType]
  );

  const TYPE_OPTION_PREFIX = 'TYPE:';

  const selectedTypeIds = useMemo(
    () =>
      model
        .filter((id) => id.startsWith(TYPE_OPTION_PREFIX))
        .map((id) => id.slice(TYPE_OPTION_PREFIX.length)) as VehicleModelType[],
    [model]
  );

  const modelOptions = useMemo(() => {
    const options: { id: string; label: React.ReactNode; header?: boolean; icon?: React.ReactNode }[] = [
      { id: 'undefined', label: 'Todos' },
    ];

    if (availableModelTypes.length) {
      options.push({ id: '__type_header__', label: 'Categoria', header: true });
      availableModelTypes.forEach((type) => {
        options.push({
          id: `${TYPE_OPTION_PREFIX}${type}`,
          icon: <span className="rioglyph rioglyph-truck text-color-gray" />,
          label: VEHICLE_MODEL_TYPE_LABEL[type],
        });
      });
      options.push({
        id: '__model_header__',
        label: selectedTypeIds.length
          ? `Modelos — ${selectedTypeIds.map((type) => VEHICLE_MODEL_TYPE_LABEL[type]).join(', ')}`
          : 'Modelos',
        header: true,
      });
    }

    const visibleDescriptions = selectedTypeIds.length
      ? uniqueModelDescriptions.filter((description) =>
          selectedTypeIds.some((type) => modelDescriptionsByModelType[type]?.has(description))
        )
      : uniqueModelDescriptions;

    visibleDescriptions.forEach((description) => options.push({ id: description, label: description }));

    return options;
  }, [availableModelTypes, selectedTypeIds, modelDescriptionsByModelType, uniqueModelDescriptions]);

  useEffect(() => {
    if (selectedTypeIds.length === 0) return;

    setModel((prev) => {
      const stillValid = prev.filter(
        (id) =>
          id === 'undefined' ||
          id.startsWith(TYPE_OPTION_PREFIX) ||
          selectedTypeIds.some((type) => modelDescriptionsByModelType[type]?.has(id))
      );
      const next = stillValid.length ? stillValid : ['undefined'];
      const unchanged = next.length === prev.length && next.every((id, i) => id === prev[i]);
      return unchanged ? prev : next;
    });
  }, [selectedTypeIds, modelDescriptionsByModelType]);

  const effectiveModelFilter = useMemo(() => {
    const manualDescriptions = model.filter((id) => id !== 'undefined' && !id.startsWith(TYPE_OPTION_PREFIX));
    if (manualDescriptions.length > 0) return manualDescriptions;

    if (selectedTypeIds.length === 0) return [];
    return uniqueModelDescriptions.filter((description) =>
      selectedTypeIds.some((type) => modelDescriptionsByModelType[type]?.has(description))
    );
  }, [model, selectedTypeIds, modelDescriptionsByModelType, uniqueModelDescriptions]);

  const lastSentModelFilterRef = useRef<string>('');
  useEffect(() => {
    if (!props.setModelFilter) return;

    const serialized = effectiveModelFilter.join('|');
    if (serialized === lastSentModelFilterRef.current) return;

    lastSentModelFilterRef.current = serialized;
    props.setModelFilter(effectiveModelFilter);
  }, [effectiveModelFilter]);

  useEffect(() => {
    fetchCpvs().then(setCpvList);
  }, []);

  const validCellsForRequest = useMemo(() => {
    if (regional.length === 0 || (regional.length === 1 && regional[0] === 'undefined')) {
      return celula.filter((c) => c !== 'undefined');
    }
    
    const validCells = new Set<number>();
    regional.forEach((r: string) => {
      if (r !== 'undefined') {
        const cellsForRegional = getCellsByRegional(Number(r));
        cellsForRegional.forEach((c) => validCells.add(c));
      }
    });
    
    return celula.filter((c) => c === 'undefined' || validCells.has(Number(c)));
  }, [regional, celula]);

  const getAvailableRegionals = () => {
    return getAllRegionals();
  };

  const getAvailableCells = () => {
    if (regional.length === 0 || (regional.length === 1 && regional[0] === 'undefined')) {
      return getAllCells();
    }
    
    const validCells = new Set<number>();
    regional.forEach((r: string) => {
      if (r !== 'undefined') {
        const cellsForRegional = getCellsByRegional(Number(r));
        cellsForRegional.forEach((c) => validCells.add(c));
      }
    });
    
    return Array.from(validCells).sort((a, b) => a - b);
  };
  
  // Mapa de placeholders por tipo de busca
  const placeholderMap: { [key: string]: string } = {
    'CHASSIS': 'Buscar por chassi',
    'CUSTOMER': 'Buscar por cliente',
    'LICENSE_PLATE': 'Buscar por placa',
    'ASSET_NAME': 'Buscar por nome do ativo',
    'PROTOCOL': 'Buscar por protocolo',
    'ASSET_TYPE': 'Buscar por tipo de ativo',
  };

  const formatDate = (date: Date) => {
    //const year = date.getFullYear();
    //const month = String(date.getMonth() + 1).padStart(2, "0");
    //const day = String(date.getDate()).padStart(2, "0");
    //return `${year}-${month}-${day}`;
    return date.toISOString()
  };

  const parseYMD = (dateLocal?: string) => {
    if (!dateLocal) return undefined;
    const date = dateLocal.split("T")[0]
    const [year, month, day] = date.split('-').map(Number);
    if (!year || !month || !day) return undefined;
    return new Date(year, month - 1, day);
  };

  const applyPresetTo = (
    preset: string,
    target: "open" | "update"
  ) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let displayStart: string | undefined;
    let displayEnd: string | undefined;
    let backendStart: string | undefined;
    let backendEnd: string | undefined;

    switch (preset) {
      case "TODAY": {
        const start = new Date(today);
        const endExclusive = new Date(today);
        endExclusive.setDate(endExclusive.getDate() + 1);
        displayStart = formatDate(start);
        displayEnd = formatDate(start);
        backendStart = formatDate(start);
        backendEnd = formatDate(endExclusive);
        break;
      }

      case "LAST_24H": {
        const start = new Date(Date.now() - 24 * 60 * 60 * 1000);
        const end = new Date();
        displayStart = formatDate(start);
        displayEnd = formatDate(end);
        backendStart = displayStart;
        backendEnd = displayEnd;
        break;
      }

      case "YESTERDAY": {
        const start = new Date(today);
        start.setDate(start.getDate() - 1);
        const endExclusive = new Date(today);
        displayStart = formatDate(start);
        displayEnd = formatDate(start);
        backendStart = formatDate(start);
        backendEnd = formatDate(endExclusive);
        break;
      }

      case "LAST_3_DAYS": {
        const start = new Date(today);
        start.setDate(today.getDate() - 2);
        const endExclusive = new Date(today);
        endExclusive.setDate(endExclusive.getDate() + 1);
        displayStart = formatDate(start);
        displayEnd = formatDate(today);
        backendStart = formatDate(start);
        backendEnd = formatDate(endExclusive);
        break;
      }

      case "LAST_10_DAYS": {
        const start = new Date(today);
        start.setDate(today.getDate() - 9);
        const endExclusive = new Date(today);
        endExclusive.setDate(endExclusive.getDate() + 1);
        displayStart = formatDate(start);
        displayEnd = formatDate(today);
        backendStart = formatDate(start);
        backendEnd = formatDate(endExclusive);
        break;
      }

      case "CLEAR":
      default:
        displayStart = undefined;
        displayEnd = undefined;
        backendStart = undefined;
        backendEnd = undefined;
    }

    if (target === "open") {
      setStartDate(displayStart);
      setEndDate(displayEnd);
      props.setStartDateFilter({ startDate: backendStart, endDate: backendEnd });
    } else {
      setUpdateStartDate(displayStart);
      setUpdateEndDate(displayEnd);
      props.setUpdateDateFilter({ startDate: backendStart, endDate: backendEnd });
    }

    if (preset === "CLEAR") {
      setDatePickerKey(prev => prev + 1);
    }
    setShowOpenDateDropdown(false);
  };

  const openDateItems = (target: "open" | "update") => [
    {
      value: "Hoje",
      onSelect: () => applyPresetTo("TODAY", target)
    },
    {
      value: "Últimas 24 horas",
      onSelect: () => applyPresetTo("LAST_24H", target)
    },
    {
      value: "Ontem",
      onSelect: () => applyPresetTo("YESTERDAY", target)
    },
    {
      value: "Últimos 3 dias",
      onSelect: () => applyPresetTo("LAST_3_DAYS", target)
    },
    {
      value: "Últimos 10 dias",
      onSelect: () => applyPresetTo("LAST_10_DAYS", target)
    },
    { divider: true },
    {
      value: <span className="text-red-600">Limpar filtro</span>,
      onSelect: () => applyPresetTo("CLEAR", target)
    }
  ];


  // Update dealership filter when selection changes
  useEffect(() => {
    props.setDealershipFilter(dealershipsSelected.map(d => d.id));
  }, [dealershipsSelected]);

  useEffect(() => {
    if (props.setCellFilter) {
      const filtered = validCellsForRequest.filter((c) => c !== 'undefined');
      props.setCellFilter(filtered);
    }
  }, [validCellsForRequest]);

  const removeDealership = (dealershipId: string) => {
    setDealershipsSelected(prev => prev.filter(d => d.id !== dealershipId));
  };

  const addDealership = (dealership: Dealership) => {
    if (!dealershipsSelected.find(d => d.id === dealership.id)) {
      setDealershipsSelected(prev => [...prev, dealership]);
    }
  };

  // This is a hack to make the type filter width equal to the criticality filter width
  useEffect(() => {
    const updateMaxWidth = () => {
      const criticalityElement = document.getElementById('criticality-filter');
      if (criticalityElement && typeContainerRef.current) {
        const criticalityWidth = criticalityElement.offsetWidth;
        setTypeMaxWidth(`${criticalityWidth}px`);
      }
    };
    updateMaxWidth();
    window.addEventListener('resize', updateMaxWidth);
    return () => window.removeEventListener('resize', updateMaxWidth);
  }, []);

  return (
    <div
      className={`flex flex-col items-center justify-between w-full py-[15px] ${isResponsiveView ? 'flex-wrap mt-4' : ''
        }`}
    >
      <div
        className={`flex gap-4 items-end w-full flex-wrap`}
      >
        <div className="mb-6 flex-shrink-0">
          {props.persona !== 'MANAGER' &&
            props.persona !== 'PARTNERSHIP' &&
            !props.finalized && (
              <NewOccurrenceButton
                partners={props.partners}
                create={props.create ?? (async () => ({ wasCreated: false }))}
                isValidStep={(
                  requiredFields: string[],
                  newOccurrence: Occurrence
                ) => props.isValidStep(requiredFields, newOccurrence)}
                persona={props.persona}
              />
            )}
        </div>

        <div
          className={`form-group ${chassis?.length && chassis?.length === 0
              ? ''
              : ''
            } ${isResponsiveView ? 'min-w-full order-1' : 'flex-1 min-w-0'}`}
        >
          <label htmlFor="search">&nbsp;</label>
          <div className="flex gap-2 w-full">
            <div className="w-40">
              <Select
                id="searchFieldName"
                options={[
                  { id: 'CHASSIS', label: 'Chassi' },
                  { id: 'CUSTOMER', label: 'Cliente' },
                  { id: 'LICENSE_PLATE', label: 'Placa' },
                  { id: 'ASSET_NAME', label: 'Nome do Ativo' },
                  { id: 'PROTOCOL', label: 'Protocolo' },
                ]}
                value={[searchFieldName]}
                onChange={(e) => {
                  setSearchFieldName(e?.id ?? 'CHASSIS');
                  setChassis('');
                  props.searchByChassis('', e?.id ?? 'CHASSIS');
                }}
              />
            </div>
            <div className="flex-1">
              <div className="input-group">
                <span className="input-group-addon">
                  <span className="rioglyph rioglyph-search" />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder={placeholderMap[searchFieldName] || `Buscar por ${searchFieldName.toLowerCase()}`}
                  id="search"
                  value={chassis || ''}
                  onChange={(e) => {
                    setChassis(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      props.searchByChassis(e.currentTarget.value, searchFieldName);
                    }
                  }}
                  onBlur={(e) => {
                    props.searchByChassis(e.currentTarget.value, searchFieldName);
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div
          className={`form-group ${isResponsiveView
              ? 'min-w-full order-2'
              : 'flex-1 min-w-0 max-w-[220px]'
            }`}
          id="criticality-filter"
        >
          <label htmlFor="criticality" className="truncate mb-[0] pb-[0]">
            Criticidade
          </label>
          <Select
            id="criticality"
            options={[
              { id: 'undefined', label: 'Todas' },
              { id: '1', label: 'Não urgente' },
              { id: '2', label: 'Pouco urgente' },
              { id: '3', label: 'Urgente' },
              { id: '4', label: 'Muito urgente' },
              { id: '5', label: 'Emergência' },
            ]}
            value={['undefined']}
            onChange={(e) => {
              props.setCriticalityFilter(e?.id ?? 'undefined');
            }}
          />
        </div>

        <div className={`form-group ${isResponsiveView
            ? 'min-w-full order-3'
            : 'flex-1 min-w-0 max-w-[200px]'
          }`}>
          <label htmlFor="regional" className="truncate mb-[0] pb-[0]">Regional</label>
          <div className="w-full">
            <Multiselect
              id="regional"
              inputAddon="rioglyph rioglyph-filter"
              options={[
                { id: 'undefined', label: 'Todas' },
                ...getAvailableRegionals().map((r) => ({ id: String(r), label: String(r) })),
              ]}
              value={regional}
              onChange={(e) => {
                if (!e || e.length === 0) {
                  setRegional(['undefined']);
                  if (props.setRegionalFilter) props.setRegionalFilter([]);
                  return;
                }
                
                if (e.includes('undefined') && !regional.includes('undefined')) {
                  setRegional(['undefined']);
                  if (props.setRegionalFilter) props.setRegionalFilter([]);
                  return;
                }
                
                const selected = e.filter((item: any) => item !== 'undefined');
                const toSet = selected.length ? selected : ['undefined'];
                setRegional(toSet);
                if (props.setRegionalFilter) props.setRegionalFilter(selected);
              }}
            />
          </div>
        </div>

        <div className={`form-group ${isResponsiveView
            ? 'min-w-full order-4'
            : 'flex-1 min-w-0 max-w-[200px]'
          }`}>
          <label htmlFor="celula" className="truncate mb-[0] pb-[0]">Célula</label>
          <div className="w-full">
            <Multiselect
              id="celula"
              inputAddon="rioglyph rioglyph-filter"
              options={[
                { id: 'undefined', label: 'Todas' },
                ...getAvailableCells().map((c) => ({ id: String(c), label: String(c) })),
              ]}
              value={celula}
              onChange={(e) => {
                if (!e || e.length === 0) {
                  setCelula(['undefined']);
                  if (props.setCellFilter) props.setCellFilter([]);
                  return;
                }
                
                if (e.includes('undefined') && !celula.includes('undefined')) {
                  setCelula(['undefined']);
                  if (props.setCellFilter) props.setCellFilter([]);
                  return;
                }
                
                const selected = e.filter((item: any) => item !== 'undefined');
                const toSet = selected.length ? selected : ['undefined'];
                setCelula(toSet);
                if (props.setCellFilter) props.setCellFilter(selected);
              }}
            />
          </div>
        </div>

        <div className={`form-group ${isResponsiveView
            ? 'min-w-full order-5'
            : 'flex-1 min-w-0 max-w-[200px]'
          }`}>
          <label htmlFor="cpv" className="truncate mb-[0] pb-[0]">Consultor (CPV)</label>
          <div className="w-full">
            <Select
              key={cpvList.length}
              id="cpv"
              useFilter
              noItemMessage="Nenhum consultor encontrado"
              placeholder="Todos"
              options={[
                { id: 'undefined', label: 'Todos' },
                ...[...cpvList].sort((a, b) => a.localeCompare(b, 'pt-BR')).map((name) => ({ id: name, label: name })),
              ]}
              value={[cpvSelected]}
              onChange={(e) => {
                const val = e?.id ?? 'undefined';
                setCpvSelected(val);
                if (props.setCpvFilter) props.setCpvFilter(val === 'undefined' ? undefined : val);
              }}
            />
          </div>
        </div>

      </div>

      <div className={`flex w-full mt-4 ${isResponsiveView ? 'flex-wrap gap-y-4' : 'items-end'}`}>
        <div className={`${isResponsiveView ? 'min-w-full' : 'flex-1 min-w-0'} form-group`} id="dealership-filter">
          <div className="w-full">
            <label htmlFor="dealerships" className="truncate mb-[0] pb-[0]">Concessionárias</label>
            <div className="w-full" style={{ width: '100%' }}>
              {/* Selected dealerships tags */}
              {dealershipsSelected.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-2">
                  {dealershipsSelected.map((dealership) => (
                    <span key={dealership.id} className="inline-flex items-center px-2 py-1 rounded-md text-xl font-medium bg-gray-100 text-gray-700 border border-gray-300">
                      {dealership.name}{dealership.city && `, ${dealership.city}`}{dealership.state && `-${dealership.state}`}, DN: {dealership.id}
                      <button type="button" className="ml-2 inline-flex items-center justify-center text-gray-700 hover:text-gray-900" onClick={() => removeDealership(dealership.id)}>
                        <span aria-hidden="true" className="text-2xl font-bold">×</span>
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <SearchAutocomplete
                id="dealershipSearch"
                placeholder="Buscar concessionária pelo nome ou DN..."
                fetchOptions={async (searchTerm: string) => await fetchDealerships(searchTerm, searchTerm, false)}
                optionLabel={{ id: 'DN', name: 'Nome' }}
                onSelectedOption={(dealership: Dealership) => addDealership(dealership)}
                overlay
                overlayClassName="!w-full"
                clearAfterSelect
                onClear={() => { }}
                valueRenderer={(option: Dealership) => `${option.name}${option.city ? `, ${option.city}` : ''}${option.state ? `-${option.state}` : ''}, DN: ${option.id}`}
                optionRenderer={(option: Dealership) => {
                  const isSelected = dealershipsSelected.find(d => d.id === option.id);
                  return (
                    <div className={`truncate ${isSelected ? 'bg-gray-100 text-gray-700' : ''}`}>
                      {isSelected && <span className="mr-1">✓</span>}
                      {option.name}{option.city && `, ${option.city}`}{option.state && `-${option.state}`}, DN: {option.id}
                      {isSelected && <span className="ml-1 text-xs">(selecionada)</span>}
                    </div>
                  );
                }}
              />
            </div>
          </div>
        </div>

        {/*

        <div className={`form-group ${isResponsiveView ? 'min-w-full' : 'flex-none min-w-[280px] max-w-[320px]'}`}>
          <label htmlFor="modelType" className="truncate mb-[0] pb-[0]">Tipo de Modelo</label>
          <div className="w-full">
            <Multiselect
              key={availableModelTypes.length}
              id="modelType"
              inputAddon="rioglyph rioglyph-filter"
              noItemMessage="Nenhum tipo de modelo encontrado"
              options={[
                { id: 'undefined', label: 'Todos' },
                ...availableModelTypes.map((type) => ({
                  id: type,
                  label: VEHICLE_MODEL_TYPE_LABEL[type],
                })),
              ]}
              value={modelType}
              onChange={(e) => {
                if (!e || e.length === 0) {
                  setModelType(['undefined']);
                  return;
                }

                if (e.includes('undefined') && !modelType.includes('undefined')) {
                  setModelType(['undefined']);
                  return;
                }

                const selected = e.filter((item: any) => item !== 'undefined');
                setModelType(selected.length ? selected : ['undefined']);
              }}
            />
          </div>
        </div>
        */}

        {!isResponsiveView ? (<div className="mx-1 h-6 text-center mt-[33px]" />) : null}

        <div className={`form-group ${isResponsiveView ? 'min-w-full' : 'flex-none min-w-[360px] max-w-[420px]'}`}>
          <label htmlFor="model" className="truncate mb-[0] pb-[0]">Categoria / Modelo</label>
          <div className="w-full">
            <Multiselect
              key={assetModels.length}
              id="model"
              inputAddon="rioglyph rioglyph-filter"
              useFilter
              noItemMessage="Nenhum modelo encontrado"
              options={modelOptions}
              value={model}
              onChange={(e) => {
                if (!e || e.length === 0) {
                  setModel(['undefined']);
                  return;
                }

                if (e.includes('undefined') && !model.includes('undefined')) {
                  setModel(['undefined']);
                  return;
                }

                const selected = e.filter((item: any) => item !== 'undefined');
                const toSet = selected.length ? selected : ['undefined'];
                setModel(toSet);
              }}
            />
          </div>
        </div>

        {!isResponsiveView ? (<div className="mx-1 h-6 text-center mt-[33px]" />) : null}

        <div className={`${isResponsiveView ? 'w-full flex flex-col gap-3' : 'flex-1 min-w-0 flex'}`}>
          <div className={`${isResponsiveView ? 'form-group min-w-full' : 'form-group w-1/2 pr-2'}`}>
            <label htmlFor="type" className="truncate mb-[0] pb-[0]">Tipo</label>
            <div className="w-full">
              <Multiselect
                id="type"
                inputAddon="rioglyph rioglyph-filter"
                options={[
                  { id: 'MAINTENANCE_TYPE', label: 'Tipo de manutenção', header: true },
                  { id: 'PREVENTIVE', label: 'Preventiva' },
                  { id: 'CORRECTIVE', label: 'Corretiva' },
                  { id: 'HAS_CAMPAIGNS', label: 'Campanhas de Campo' },
                  { id: 'OCCURRENCE_TYPE', label: 'Tipo de ocorrência', header: true },
                  { id: 'ASSISTANCE', label: 'Socorro mecânico' },
                  { id: 'WINCH', label: 'Guincho' },
                  { id: 'IN_DEALERSHIP', label: 'Na concessionária' },
                  { id: 'CLAIM', label: 'Sinistro' },
                ]}
                value={props.typeFilter}
                onChange={(e) => props.setTypeFilter(e ? e.filter((item: any) => item !== 'undefined') : [])}
              />
            </div>
          </div>

          <div className={`form-group min-w-[210px] ${isResponsiveView ? 'min-w-full' : ''}`} id="criticality-filter">
            <label htmlFor="isAlert" className="truncate mb-[0] pb-[0]">
              Em alerta
            </label>
            <Select
              id="isAlert"
              options={[
                { id: 'undefined', label: 'Todas' },
                { id: 'true', label: 'Sim' },
                { id: 'false', label: 'Não' },
              ]}
              value={['undefined']}
              onChange={(e) => {
                props.setIsAlertFilter(e?.id ?? 'undefined');
              }}
            />
          </div>
        </div>
      </div>

      <div className={`flex w-full mt-4 ${isResponsiveView ? 'flex-wrap gap-y-4' : 'items-start'}`}>
        <div className={`flex gap-3 items-center ${isResponsiveView ? 'w-full' : 'flex-1 min-w-0'}`}>
          <div className="form-group w-auto">
            <div className="relative">
              <ButtonDropdown
                className="mt-3"
                onOpen={() => setShowOpenDateDropdown(!showOpenDateDropdown)}
                onClose={() => setShowOpenDateDropdown(false)}
                title={<span className="rioglyph rioglyph-settings" aria-hidden="true" />}
                iconOnly
                items={openDateItems("open")}
              >
              </ButtonDropdown>
            </div>
          </div>
          <div className={`form-group w-full min-w-auto ${!startDate && endDate ? 'has-feedback has-error' : ''}`}>
            <label htmlFor="openDateStart" className="truncate mb-[0] pb-[0]">Data de abertura</label>
            <DatePicker
              key={`start-date-${datePickerKey}`}
              id="openDateStart"
              value={startDate ? parseYMD(startDate) : undefined}
              onChange={(e: any) => {
                if (typeof e === 'string' && e.length) return;
                if (typeof e === 'string') {
                  setStartDate(undefined);
                  props.setStartDateFilter({ startDate: undefined, endDate });
                  return;
                }
                const dateEvent = e
                const newStartDate = dateEvent?.toISOString();

                if(!endDate) {
                  setStartDate(newStartDate)
                  return
                }

                if (endDate && newStartDate) {
                  const startDateObj = dateEvent.toDate();
                  const endDateObj = new Date(endDate);
                  if (startDateObj && endDateObj && startDateObj > endDateObj) {
                    setDatePickerKey(prev => prev + 1);
                    return;
                  }
                }
                setStartDate(newStartDate);
                props.setStartDateFilter({ startDate: newStartDate, endDate });
              }}
              locale="pt-BR"
              dateFormat="DD-MM-YYYY"
              timeFormat={false}
              mandatory={false}
              clearableInput={true}
            />
          </div>

          <div><span className="text-[#697A94]">Até</span></div>

          <div className={`form-group w-full ${startDate && !endDate ? "has-feedback has-error" : ""} ${endDateError ? "has-feedback has-error" : ""}`}>
            <label htmlFor="openDateEnd" className="truncate mb-[0] pb-[0]">&nbsp;</label>
            <DatePicker
              key={`end-date-${datePickerKey}`}
              id="openDateEnd"
              value={endDate ? parseYMD(endDate) : undefined}
              onChange={(e: any) => {
                if (typeof e === 'string' && e.length) return;
                if (typeof e === 'string') {
                  setEndDate(undefined);
                  setEndDateError(null);
                  props.setStartDateFilter({ startDate, endDate: undefined });
                  return;
                }
                const newEndDate = e?.toISOString();
                if(!startDate) {
                    setEndDate(newEndDate)
                    return;
                }
                if (startDate && e) {
                  const startDateObj = new Date(startDate);
                  const endDateObj = e.toDate();
                  if (startDateObj && endDateObj && endDateObj < startDateObj) {
                    setEndDateError('A data final não pode ser anterior à data inicial');
                    setDatePickerKey(prev => prev + 1);
                    setTimeout(() => setEndDateError(null), 3000);
                    return;
                  }
                }
                setEndDateError(null);
                // Importante para manter o range de data correto
                
                setEndDate(newEndDate);

                if(e) {
                  e.day(e.day() + 1)
                }
                const newEndDateSend = e?.toISOString()
                props.setStartDateFilter({ startDate, endDate: newEndDateSend });
              }}
              locale="pt-BR"
              dateFormat="DD-MM-YYYY"
              timeFormat={false}
              mandatory={false}
              clearableInput={true}
            />
            {endDateError && (<span className="help-block text-red-600 text-sm">{endDateError}</span>)}
          </div>
        </div>

        {!isResponsiveView ? (<div className="border border-left-only border-width-2 mx-5 h-6 text-center mt-[33px]" />) : null}

        <div className={`flex gap-3 items-center ${isResponsiveView ? 'w-full' : 'flex-1 min-w-0'}`}>
          <div className="form-group w-auto">
            <div className="relative">
              <ButtonDropdown
                className="mt-3"
                onOpen={() => setShowOpenDateDropdown(!showOpenDateDropdown)}
                onClose={() => setShowOpenDateDropdown(false)}
                title={<span className="rioglyph rioglyph-settings" aria-hidden="true" />}
                iconOnly
                items={openDateItems("update")}
              />
            </div>
          </div>
          <div className={`form-group w-full ${!updateStartDate && updateEndDate ? 'has-feedback has-error' : ''}`}>
            <label htmlFor="updateDateStart" className="truncate mb-[0] pb-[0]">Data de atualização</label>
            <DatePicker
              key={`update-start-date-${datePickerKey}`}
              id="updateDateStart"
              value={updateStartDate ? parseYMD(updateStartDate) : undefined}
              onChange={(e: any) => {
                if (typeof e === 'string' && e.length) return;
                if (typeof e === 'string') {
                  setUpdateStartDate(undefined);
                  props.setUpdateDateFilter({ startDate: undefined, endDate: updateEndDate });
                  return;
                }
                const newStartDate = e?.toISOString();
                if(!updateEndDate) {
                  setUpdateStartDate(newStartDate);
                  return;
                }
                if (updateEndDate && newStartDate) {
                  const startDateObj = e.toDate();
                  const endDateObj = new Date(updateEndDate);
                  if (startDateObj && endDateObj && startDateObj > endDateObj) {
                    setDatePickerKey(prev => prev + 1);
                    return;
                  }
                }
                setUpdateStartDate(newStartDate);
                props.setUpdateDateFilter({ startDate: newStartDate, endDate: updateEndDate });
              }}
              locale="pt-BR"
              dateFormat="DD-MM-YYYY"
              timeFormat={false}
              mandatory={false}
              clearableInput={true}
            />
          </div>

          <div><span className="text-[#697A94]">Até</span></div>

          <div className={`form-group w-full ${updateStartDate && !updateEndDate ? "has-feedback has-error" : ""} ${updateEndDateError ? "has-feedback has-error" : ""}`}>
            <label htmlFor="updateDateEnd" className="truncate mb-[0] pb-[0]">&nbsp;</label>
            <DatePicker
              key={`update-end-date-${datePickerKey}`}
              id="updateDateEnd"
              value={updateEndDate ? parseYMD(updateEndDate) : undefined}
              onChange={(e: any) => {
                if (typeof e === 'string' && e.length) return;
                if (typeof e === 'string') {
                  setUpdateEndDate(undefined);
                  setUpdateEndDateError(null);
                  props.setUpdateDateFilter({ startDate: updateStartDate, endDate: undefined });
                  return;
                }
                const newEndDate = e?.toISOString();
                if(!updateStartDate) {
                  setUpdateEndDate(newEndDate);
                  return;
                }
                if (updateStartDate && newEndDate) {
                  const startDateObj = new Date(updateStartDate);
                  const endDateObj = e.toDate();
                  if (startDateObj && endDateObj && endDateObj < startDateObj) {
                    setUpdateEndDateError('A data final não pode ser anterior à data inicial');
                    setDatePickerKey(prev => prev + 1);
                    setTimeout(() => setUpdateEndDateError(null), 3000);
                    return;
                  }
                }
                setUpdateEndDateError(null);
                setUpdateEndDate(newEndDate);

                if(e) {
                  e.day(e.day() + 1)
                }
                const newEndDateSend = e?.toISOString()

                props.setUpdateDateFilter({ startDate: updateStartDate, endDate: newEndDateSend });
              }}
              locale="pt-BR"
              dateFormat="DD-MM-YYYY"
              timeFormat={false}
              mandatory={false}
              clearableInput={true}
            />
            {updateEndDateError && (<span className="help-block text-red-600 text-sm">{updateEndDateError}</span>)}
          </div>
        </div>

        {!isResponsiveView ? (<div className="border border-left-only border-width-2 mx-5 h-6 text-center mt-[33px]" />) : null}
        <div className="mt-[17px] ml-[-15px]">
          <TypeButtonGroup viewType={props.viewType} setViewType={props.setViewType} exportButton={props.exportButton} copyTokenButton={props.copyTokenButton} />
        </div>
      </div>
    </div>
  );
};

export default FilterRow;
